import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import { chartColors, formatCurrency, formatPercent } from './chartUtils'

export interface DonutChartData {
  name: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutChartData[];
  title?: string;
  showLegend?: boolean;
  showLabels?: boolean;
  innerRadius?: number;
  outerRadius?: number;
  centerLabel?: string;
  centerValue?: string;
  onSegmentClick?: (data: DonutChartData) => void;
  height?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: DonutChartData & { percent: number } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.[0]) return null;

  const data = payload[0].payload;
  const total = payload[0].payload.percent;

  return (
    <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-stone/10">
      <p className="font-medium text-charcoal">{data.name}</p>
      <p className="text-forest font-semibold tabular-nums">{formatCurrency(data.value)}</p>
      <p className="text-sm text-stone">{formatPercent(total * 100)}</p>
    </div>
  );
}

interface CustomLegendProps {
  payload?: Array<{
    value: string;
    color: string;
    payload: { value: number };
  }>;
}

function CustomLegend({ payload }: CustomLegendProps) {
  if (!payload) return null;

  return (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 mt-4">
      {payload.map((entry, index) => (
        <li key={index} className="flex items-center gap-2 text-sm">
          <span
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-charcoal">{entry.value}</span>
          <span className="text-stone tabular-nums">
            {formatCurrency(entry.payload.value)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function DonutChart({
  data,
  title,
  showLegend = true,
  innerRadius = 60,
  outerRadius = 90,
  centerLabel,
  centerValue,
  onSegmentClick,
  height = 300,
}: DonutChartProps) {
  // Assign colors to data if not provided
  const coloredData = data.map((item, index) => ({
    ...item,
    color: item.color || chartColors.primary[index % chartColors.primary.length],
  }));

  const total = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-semibold text-charcoal mb-4">{title}</h3>
      )}
      <div style={{ height }} className="relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={coloredData}
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              dataKey="value"
              onClick={(_, index) => onSegmentClick?.(coloredData[index])}
              className={onSegmentClick ? 'cursor-pointer' : ''}
            >
              {coloredData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke="white"
                  strokeWidth={2}
                  className="transition-opacity hover:opacity-80"
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            {showLegend && <Legend content={<CustomLegend />} />}
          </PieChart>
        </ResponsiveContainer>

        {/* Center label */}
        {(centerLabel || centerValue) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center" style={{ marginTop: showLegend ? -40 : 0 }}>
              {centerValue && (
                <p className="text-xl font-bold text-forest-dark tabular-nums">
                  {centerValue}
                </p>
              )}
              {centerLabel && (
                <p className="text-sm text-stone">{centerLabel}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {data.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-stone">No data available</p>
        </div>
      )}
    </div>
  );
}
